import React, { useState } from 'react';
import './PropertiesPanel.css';
import { CanvasElement, TextElement, ImageElement, RectElement, CircleElement, LineElement, TriangleElement } from '../types';
import FontPicker from './FontPicker';

interface PropertiesPanelProps {
  selectedElement: CanvasElement | null;
  background: string;
  onUpdate: (id: string, patch: Partial<CanvasElement>) => void;
  onUpdateLive: (id: string, patch: Partial<CanvasElement>) => void;
  onSetBackground: (color: string) => void;
  onSetBackgroundLive: (color: string) => void;
}

// ─── Shared primitives ────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; icon: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title, icon, defaultOpen = true, children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pp-section">
      <button className="pp-section-btn" onClick={() => setOpen(v => !v)}>
        <span className="pp-section-icon">{icon}</span>
        <span className="pp-section-title">{title}</span>
        <svg className={`pp-section-chevron ${open ? 'open' : 'closed'}`}
          width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="pp-section-body">{children}</div>}
    </div>
  );
};

const SliderRow: React.FC<{
  label: string; display: string;
  value: number; min: number; max: number; step: number;
  onChange: (v: number) => void;   // live preview — no history entry
  onCommit: (v: number) => void;   // pointer-up — commits one history entry
}> = ({ label, display, value, min, max, step, onChange, onCommit }) => (
  <div className="pp-slider-row">
    <div className="pp-slider-meta">
      <span className="pp-slider-label">{label}</span>
      <span className="pp-slider-value">{display}</span>
    </div>
    <input className="pp-slider" type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      onPointerUp={e => onCommit(parseFloat((e.target as HTMLInputElement).value))} />
  </div>
);

const ColorRow: React.FC<{
  label?: string; value: string;
  onChange: (v: string) => void;   // commits to history
  onLive?: (v: string) => void;    // live preview from color picker — no history
  placeholder?: string;
}> = ({ label, value, onChange, onLive, placeholder }) => (
  <div>
    {label && <span className="pp-label">{label}</span>}
    <div className="pp-color-row">
      <div className="pp-color-swatch-wrap">
        <div className="pp-color-swatch-display" style={{ background: value || '#e4e4e7' }} />
        <input type="color" value={value || '#000000'}
          onChange={e => (onLive ?? onChange)(e.target.value)}
          onBlur={e => onChange(e.target.value)}
          className="pp-color-swatch-input" />
      </div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || '#000000'} className="pp-input pp-input-mono" />
    </div>
  </div>
);

const Seg: React.FC<{
  options: { value: string; label: React.ReactNode }[];
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="pp-seg">
    {options.map(opt => (
      <button key={String(opt.value)}
        className={`pp-seg-btn ${value === opt.value ? 'active' : ''}`}
        onClick={() => onChange(opt.value)}>
        {opt.label}
      </button>
    ))}
  </div>
);

const Toggle: React.FC<{ label: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, value, onChange }) => (
  <div className="pp-toggle-row">
    <span className="pp-toggle-label">{label}</span>
    <button className={`pp-toggle ${value ? 'on' : 'off'}`} onClick={() => onChange(!value)}>
      <div className="pp-toggle-thumb" />
    </button>
  </div>
);

// ─── Panel ────────────────────────────────────────────────────────────────────

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedElement, background, onUpdate, onUpdateLive, onSetBackground, onSetBackgroundLive,
}) => {
  if (!selectedElement) {
    return (
      <div className="pp-root">
        <div className="pp-header"><span className="pp-header-label">Canvas</span></div>
        <div className="pp-scroll">
          <div className="pp-block">
            <ColorRow label="Background" value={background}
              onChange={onSetBackground} onLive={onSetBackgroundLive} />
          </div>
          <div className="pp-empty">
            <p className="pp-empty-hint">Select an element on the canvas to edit its properties.</p>
          </div>
        </div>
      </div>
    );
  }

  const el = selectedElement;
  const up     = (patch: Partial<CanvasElement>) => onUpdate(el.id, patch);
  const upLive = (patch: Partial<CanvasElement>) => onUpdateLive(el.id, patch);

  return (
    <div className="pp-root">
      <div className="pp-header">
        <span className="pp-header-label">{el.type} properties</span>
      </div>

      <div className="pp-scroll">

        {/* ── TEXT ── */}
        {el.type === 'text' && (() => {
          const t = el as TextElement;
          return (
            <>
              <div className="pp-block">
                <span className="pp-label">Content</span>
                <textarea className="pp-textarea" value={t.text} rows={3}
                  style={{ fontFamily: t.fontFamily }}
                  onChange={e => up({ text: e.target.value } as Partial<TextElement>)} />
              </div>

              <Section title="Typography" icon="Aa">
                <div>
                  <span className="pp-label">Font</span>
                  <FontPicker value={t.fontFamily} onChange={font => up({ fontFamily: font } as Partial<TextElement>)} />
                </div>
                <SliderRow label="Size" display={`${t.fontSize}px`}
                  value={t.fontSize} min={8} max={200} step={1}
                  onChange={v => upLive({ fontSize: v } as Partial<TextElement>)}
                  onCommit={v => up({ fontSize: v } as Partial<TextElement>)} />
                <ColorRow label="Color" value={t.fill}
                  onChange={v => up({ fill: v } as Partial<TextElement>)}
                  onLive={v => upLive({ fill: v } as Partial<TextElement>)} />
                <div className="pp-grid-2">
                  <div>
                    <span className="pp-label">Style</span>
                    <Seg value={t.fontStyle}
                      onChange={v => up({ fontStyle: v as TextElement['fontStyle'] } as Partial<TextElement>)}
                      options={[
                        { value: 'normal',      label: 'N' },
                        { value: 'bold',        label: <b>B</b> },
                        { value: 'italic',      label: <i>I</i> },
                        { value: 'bold italic', label: <b><i>BI</i></b> },
                      ]} />
                  </div>
                  <div>
                    <span className="pp-label">Align</span>
                    <Seg value={t.align}
                      onChange={v => up({ align: v as TextElement['align'] } as Partial<TextElement>)}
                      options={[
                        { value: 'left',   label: '←' },
                        { value: 'center', label: '↔' },
                        { value: 'right',  label: '→' },
                      ]} />
                  </div>
                </div>
                <div>
                  <span className="pp-label">Case</span>
                  <Seg value={t.textTransform ?? 'none'}
                    onChange={v => up({ textTransform: v as TextElement['textTransform'] } as Partial<TextElement>)}
                    options={[
                      { value: 'none',       label: '—' },
                      { value: 'uppercase',  label: 'AA' },
                      { value: 'lowercase',  label: 'aa' },
                      { value: 'capitalize', label: 'Aa' },
                    ]} />
                </div>
              </Section>

              <Section title="Spacing" icon="↔" defaultOpen={false}>
                <SliderRow label="Letter Spacing" display={`${t.letterSpacing ?? 0}px`}
                  value={t.letterSpacing ?? 0} min={-10} max={60} step={0.5}
                  onChange={v => upLive({ letterSpacing: v } as Partial<TextElement>)}
                  onCommit={v => up({ letterSpacing: v } as Partial<TextElement>)} />
                <SliderRow label="Line Height" display={`${(t.lineHeight ?? 1.2).toFixed(2)}×`}
                  value={t.lineHeight ?? 1.2} min={0.8} max={3} step={0.05}
                  onChange={v => upLive({ lineHeight: v } as Partial<TextElement>)}
                  onCommit={v => up({ lineHeight: v } as Partial<TextElement>)} />
              </Section>

              <Section title="Outline" icon="◻" defaultOpen={false}>
                <ColorRow label="Color" value={t.strokeColor || ''} placeholder="none"
                  onChange={v => up({ strokeColor: v } as Partial<TextElement>)}
                  onLive={v => upLive({ strokeColor: v } as Partial<TextElement>)} />
                <SliderRow label="Width" display={`${t.strokeWidth ?? 0}px`}
                  value={t.strokeWidth ?? 0} min={0} max={20} step={0.5}
                  onChange={v => upLive({ strokeWidth: v } as Partial<TextElement>)}
                  onCommit={v => up({ strokeWidth: v } as Partial<TextElement>)} />
                <div>
                  <span className="pp-label">Position</span>
                  <Seg value={t.strokePosition ?? 'outer'}
                    onChange={v => up({ strokePosition: v as TextElement['strokePosition'] } as Partial<TextElement>)}
                    options={[
                      { value: 'outer',  label: 'Outer' },
                      { value: 'center', label: 'Center' },
                    ]} />
                </div>
              </Section>

              <Section title="Shadow" icon="◈" defaultOpen={false}>
                <Toggle label="Enable shadow"
                  value={t.shadowEnabled ?? false}
                  onChange={v => up({ shadowEnabled: v } as Partial<TextElement>)} />
                {(t.shadowEnabled ?? false) && (
                  <>
                    <ColorRow label="Color" value={t.shadowColor ?? '#000000'}
                      onChange={v => up({ shadowColor: v } as Partial<TextElement>)}
                      onLive={v => upLive({ shadowColor: v } as Partial<TextElement>)} />
                    <SliderRow label="Blur" display={`${t.shadowBlur ?? 10}px`}
                      value={t.shadowBlur ?? 10} min={0} max={50} step={1}
                      onChange={v => upLive({ shadowBlur: v } as Partial<TextElement>)}
                      onCommit={v => up({ shadowBlur: v } as Partial<TextElement>)} />
                    <SliderRow label="Offset X" display={`${t.shadowOffsetX ?? 0}px`}
                      value={t.shadowOffsetX ?? 0} min={-30} max={30} step={1}
                      onChange={v => upLive({ shadowOffsetX: v } as Partial<TextElement>)}
                      onCommit={v => up({ shadowOffsetX: v } as Partial<TextElement>)} />
                    <SliderRow label="Offset Y" display={`${t.shadowOffsetY ?? 5}px`}
                      value={t.shadowOffsetY ?? 5} min={-30} max={30} step={1}
                      onChange={v => upLive({ shadowOffsetY: v } as Partial<TextElement>)}
                      onCommit={v => up({ shadowOffsetY: v } as Partial<TextElement>)} />
                  </>
                )}
              </Section>
            </>
          );
        })()}

        {/* ── IMAGE ── */}
        {el.type === 'image' && (() => {
          const img = el as ImageElement;
          const upImg     = (patch: Partial<ImageElement>) => up(patch as Partial<typeof el>);
          const upImgLive = (patch: Partial<ImageElement>) => upLive(patch as Partial<typeof el>);
          return (
            <>
              <Section title="Adjustments" icon="◑">
                <SliderRow label="Brightness" display={`${Math.round((img.brightness ?? 0) * 100)}`}
                  value={img.brightness ?? 0} min={-1} max={1} step={0.01}
                  onChange={v => upImgLive({ brightness: v })}
                  onCommit={v => upImg({ brightness: v })} />
                <SliderRow label="Contrast" display={`${Math.round(img.contrast ?? 0)}`}
                  value={img.contrast ?? 0} min={-100} max={100} step={1}
                  onChange={v => upImgLive({ contrast: v })}
                  onCommit={v => upImg({ contrast: v })} />
                <SliderRow label="Saturation" display={`${Math.round((img.saturation ?? 0) * 100)}`}
                  value={img.saturation ?? 0} min={-2} max={2} step={0.01}
                  onChange={v => upImgLive({ saturation: v })}
                  onCommit={v => upImg({ saturation: v })} />
              </Section>

              <Section title="Temperature" icon="◎" defaultOpen={false}>
                <div className="pp-temp-row">
                  <span className="pp-temp-label cool">❄ Cool</span>
                  <input
                    type="range" className="pp-slider pp-slider-temp"
                    value={img.temperature ?? 0} min={-100} max={100} step={1}
                    onChange={e => upImgLive({ temperature: Number(e.target.value) })}
                    onPointerUp={e => upImg({ temperature: Number((e.target as HTMLInputElement).value) })}
                  />
                  <span className="pp-temp-label warm">☀ Warm</span>
                </div>
                <div className="pp-temp-value">
                  {(img.temperature ?? 0) === 0
                    ? 'Neutral'
                    : (img.temperature ?? 0) > 0
                      ? `+${img.temperature} Warm`
                      : `${img.temperature} Cool`}
                </div>
              </Section>

              <Section title="Effects" icon="✦" defaultOpen={false}>
                <SliderRow label="Blur" display={`${img.blur ?? 0}px`}
                  value={img.blur ?? 0} min={0} max={20} step={0.5}
                  onChange={v => upImgLive({ blur: v })}
                  onCommit={v => upImg({ blur: v })} />
                <Toggle label="Grayscale" value={!!img.grayscale}
                  onChange={v => upImg({ grayscale: v })} />
                <Toggle label="Sepia" value={!!img.sepia}
                  onChange={v => upImg({ sepia: v })} />
              </Section>

              <Section title="Image" icon="⬚" defaultOpen={false}>
                <span className="pp-label">Flip</span>
                <div className="pp-grid-2">
                  <button className={`pp-flip-btn ${img.flipX ? 'active' : ''}`}
                    onClick={() => upImg({ flipX: !img.flipX })}>
                    ↔ Horizontal
                  </button>
                  <button className={`pp-flip-btn ${img.flipY ? 'active' : ''}`}
                    onClick={() => upImg({ flipY: !img.flipY })}>
                    ↕ Vertical
                  </button>
                </div>
              </Section>
            </>
          );
        })()}

        {/* ── RECT ── */}
        {el.type === 'rect' && (() => {
          const r = el as RectElement;
          return (
            <Section title="Rectangle" icon="▭">
              <ColorRow label="Fill" value={r.fill}
                onChange={v => up({ fill: v } as Partial<RectElement>)}
                onLive={v => upLive({ fill: v } as Partial<RectElement>)} />
              <ColorRow label="Stroke" value={r.stroke || ''} placeholder="none"
                onChange={v => up({ stroke: v } as Partial<RectElement>)}
                onLive={v => upLive({ stroke: v } as Partial<RectElement>)} />
              <SliderRow label="Stroke Width" display={`${r.strokeWidth}px`}
                value={r.strokeWidth} min={0} max={20} step={1}
                onChange={v => upLive({ strokeWidth: v } as Partial<RectElement>)}
                onCommit={v => up({ strokeWidth: v } as Partial<RectElement>)} />
              <SliderRow label="Corner Radius" display={`${r.cornerRadius}px`}
                value={r.cornerRadius} min={0} max={60} step={1}
                onChange={v => upLive({ cornerRadius: v } as Partial<RectElement>)}
                onCommit={v => up({ cornerRadius: v } as Partial<RectElement>)} />
            </Section>
          );
        })()}

        {/* ── CIRCLE ── */}
        {el.type === 'circle' && (() => {
          const c = el as CircleElement;
          return (
            <Section title="Ellipse" icon="○">
              <ColorRow label="Fill" value={c.fill}
                onChange={v => up({ fill: v } as Partial<CircleElement>)}
                onLive={v => upLive({ fill: v } as Partial<CircleElement>)} />
              <ColorRow label="Stroke" value={c.stroke || ''} placeholder="none"
                onChange={v => up({ stroke: v } as Partial<CircleElement>)}
                onLive={v => upLive({ stroke: v } as Partial<CircleElement>)} />
              <SliderRow label="Stroke Width" display={`${c.strokeWidth}px`}
                value={c.strokeWidth} min={0} max={20} step={1}
                onChange={v => upLive({ strokeWidth: v } as Partial<CircleElement>)}
                onCommit={v => up({ strokeWidth: v } as Partial<CircleElement>)} />
            </Section>
          );
        })()}

        {/* ── TRIANGLE ── */}
        {el.type === 'triangle' && (() => {
          const t = el as TriangleElement;
          return (
            <Section title="Triangle" icon="△">
              <ColorRow label="Fill" value={t.fill}
                onChange={v => up({ fill: v } as Partial<TriangleElement>)}
                onLive={v => upLive({ fill: v } as Partial<TriangleElement>)} />
              <ColorRow label="Stroke" value={t.stroke || ''} placeholder="none"
                onChange={v => up({ stroke: v } as Partial<TriangleElement>)}
                onLive={v => upLive({ stroke: v } as Partial<TriangleElement>)} />
              <SliderRow label="Stroke Width" display={`${t.strokeWidth}px`}
                value={t.strokeWidth} min={0} max={20} step={1}
                onChange={v => upLive({ strokeWidth: v } as Partial<TriangleElement>)}
                onCommit={v => up({ strokeWidth: v } as Partial<TriangleElement>)} />
            </Section>
          );
        })()}

        {/* ── LINE ── */}
        {el.type === 'line' && (() => {
          const l = el as LineElement;
          return (
            <Section title="Line" icon="—">
              <ColorRow label="Color" value={l.stroke}
                onChange={v => up({ stroke: v } as Partial<LineElement>)}
                onLive={v => upLive({ stroke: v } as Partial<LineElement>)} />
              <SliderRow label="Thickness" display={`${l.strokeWidth}px`}
                value={l.strokeWidth} min={1} max={40} step={1}
                onChange={v => upLive({ strokeWidth: v } as Partial<LineElement>)}
                onCommit={v => up({ strokeWidth: v } as Partial<LineElement>)} />
              <div>
                <span className="pp-label">Cap style</span>
                <Seg value={l.cap}
                  onChange={v => up({ cap: v as LineElement['cap'] } as Partial<LineElement>)}
                  options={[
                    { value: 'butt',   label: 'Flat' },
                    { value: 'round',  label: 'Round' },
                    { value: 'square', label: 'Square' },
                  ]} />
              </div>
            </Section>
          );
        })()}

        {/* ── Appearance ── */}
        <Section title="Appearance" icon="◐">
          <SliderRow label="Opacity" display={`${Math.round(el.opacity * 100)}%`}
            value={el.opacity} min={0} max={1} step={0.01}
            onChange={v => upLive({ opacity: v })}
            onCommit={v => up({ opacity: v })} />
          <SliderRow label="Rotation" display={`${Math.round(el.rotation)}°`}
            value={el.rotation} min={-180} max={180} step={1}
            onChange={v => upLive({ rotation: v })}
            onCommit={v => up({ rotation: v })} />
        </Section>

        {/* ── Position & Size ── */}
        <Section title="Position & Size" icon="⤢" defaultOpen={false}>
          <div className="pp-grid-2">
            {([
              { key: 'x',      label: 'X', value: el.x },
              { key: 'y',      label: 'Y', value: el.y },
              { key: 'width',  label: el.type === 'line' ? 'Length' : 'W', value: el.width },
              ...(el.type !== 'line' ? [{ key: 'height', label: 'H', value: el.height }] : []),
            ] as { key: string; label: string; value: number }[]).map(({ key, label, value }) => (
              <div key={key}>
                <span className="pp-label">{label}</span>
                <input type="number" value={Math.round(value)}
                  onChange={e => up({ [key]: parseFloat(e.target.value) })}
                  className="pp-input pp-input-mono" />
              </div>
            ))}
          </div>
        </Section>

      </div>
    </div>
  );
};

export default PropertiesPanel;
